import { addToast, Table } from '@octano/global-ui';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Col } from 'reactstrap';
import TableEmptyContent from '../../../../components/text/TableEmptyContent';
import { TranslationsKeys } from '../../../../locales/translations';
import {
  downloadFromBlob,
  readErrorMessageFromBlob,
} from '../../../../utils/blob';
import { getTemplate } from '../api';
import useColumns from '../hooks/useColumns';
import NoContentModal, { NoContentModalRef } from './NoContentModal';
import SurveyResultFilters from './SurveyResultFilters';
import SurveyResultFiltersLoader from './SurveyResultFiltersLoader';
import {
  ITEMS_PER_PAGE_DEFAULT,
  useSurveyResultsListLoader,
} from './SurveyResultsTableLoader';

export default function SurveyResultsTable() {
  const { data, loading, query, updateQuery } = useSurveyResultsListLoader();
  const { t } = useTranslation(
    TranslationsKeys.MANAGEMENT_TOOLS_SURVEY_RESULTS,
  );
  const [isLoading, setIsLoading] = useState(false);

  const confirmRef = useRef<NoContentModalRef>(null);

  const requestDownload = async (param: {
    surveyId: number;
    periodName: string;
  }) => {
    setIsLoading(true);
    const res = await getTemplate({ surveyId: param.surveyId });
    if (res.error) {
      // El 409 (encuesta sin preguntas cerradas para promediar) trae un mensaje
      // del servidor pensado para el usuario: se muestra tal cual.
      const serverMessage =
        res.error.status === 409
          ? await readErrorMessageFromBlob(res.error.data)
          : null;

      addToast({
        icon: 'error',
        color: 'danger',
        text: serverMessage ?? 'Error al descargar la plantilla',
      });
      setIsLoading(false);
      return;
    }
    if (res.status === 204) {
      confirmRef.current?.openModal(param);
      setIsLoading(false);
      return;
    }
    const blob = new Blob([res.data], { type: 'vnd.ms-excel' });
    const fileName = `Resultados encuesta ev. docente_ periodo ${param.periodName}.xlsx`;
    downloadFromBlob(blob, fileName);
    setIsLoading(false);
  };

  const columnsOthers = useColumns({
    isLoading,
    onClickEdit: (row) => {
      requestDownload({ surveyId: row.id, periodName: row.periodName });
    },
  });

  return (
    <>
      <Card className="m-3 p-3">
        <div className="mb-3">
          <p className="fs-16 text-light mt-3 ">
            {t('surveyResultList.descriptionText')}
          </p>
        </div>

        <div className="mb-5">
          <SurveyResultFiltersLoader>
            <SurveyResultFilters
              onSubmit={(filters) => updateQuery({ ...query, ...filters })}
            />
          </SurveyResultFiltersLoader>
          <Table
            columns={columnsOthers}
            data={data?.data ?? []}
            isLoadingResults={loading}
            loadingView={{
              title: t('surveyResultList.loadingText'),
              subtitle: t('surveyResultList.loadingSubText'),
            }}
            noResultsText={
              <TableEmptyContent
                title={t('surveyResultList.emptyText')}
                subtitle={t('surveyResultList.emptySubText')}
              />
            }
            pagination={{
              currentPage: query.page,
              totalItems: data?.total ?? 0,
              totalPages: data?.total_pages ?? 0,
              itemsPerPage: ITEMS_PER_PAGE_DEFAULT,
              onChangePage: (page: number) => updateQuery({ ...query, page }),
            }}
            footComponent={() => <Col style={{ minHeight: '70px' }} />}
          />
        </div>
      </Card>

      <NoContentModal
        ref={confirmRef}
        onConfirm={() => {}}
        onDecline={() => {}}
      />
    </>
  );
}
